import { apiVariables } from './api/apiVariables';
import { deleteWithAuth, getWithAuth, postWithAuthLocal } from './api/client';
import type { CreateSavedListRequest, SavedListDto, SavedListMember } from '../types/savedLists';

type ApiResponse<T> = {
  success?: boolean;
  statusCode?: number;
  message?: string;
  data?: T;
};

type BackendSkill = {
  skillName?: string;
};

type BackendSavedListMember = {
  userId?: string;
  username?: string;
  fullName?: string;
  itSpeciality?: string;
  itSpecialtyName?: string;
  totalExperienceInYears?: number;
  availability?: string;
  currentOrganization?: string;
  photoId?: number | null;
  profilePhotoId?: number | null;
  skills?: BackendSkill[];
};

type BackendSavedListDto = {
  savedListId?: number;
  id?: string;
  listName?: string;
  description?: string;
  createdBy?: string;
  createdByUserId?: string;
  createdByName?: string;
  createdAt?: string;
  memberCount?: number;
  memberUserIds?: string[];
  members?: BackendSavedListMember[];
};

const SAVED_LISTS_CHANGED_EVENT = 'savedLists:changed';

const normalizeMember = (member: BackendSavedListMember): SavedListMember => ({
  userId: String(member.userId || ''),
  userName: member.username,
  fullName: member.fullName,
  itSpecialtyName: member.itSpecialtyName || member.itSpeciality,
  totalExperienceInYears: member.totalExperienceInYears,
  currentOrganization: member.currentOrganization,
  availabilities: member.availability ? [{ availabilityTypeName: member.availability }] : undefined,
  photoId: member.profilePhotoId ?? member.photoId ?? undefined,
  profilePhotoId: member.profilePhotoId ?? member.photoId ?? undefined,
  skills: member.skills || undefined,
});

const normalizeSavedList = (list: BackendSavedListDto): SavedListDto => {
  const members = Array.isArray(list.members) ? list.members.map(normalizeMember) : [];
  const memberUserIds = Array.isArray(list.memberUserIds)
    ? list.memberUserIds.map(String)
    : members.map((member) => member.userId).filter(Boolean);
  const id = String(list.id || list.savedListId || '');
  const createdByUserId = String(list.createdByUserId || list.createdBy || '');

  return {
    id,
    savedListId: list.savedListId,
    listName: list.listName || '',
    description: list.description || '',
    memberUserIds,
    memberCount: list.memberCount ?? memberUserIds.length,
    members,
    createdByUserId,
    createdBy: list.createdBy,
    createdByName: list.createdByName,
    createdAt: list.createdAt || new Date().toISOString(),
    skills: list.members?.flatMap((member) => member.skills || []) || undefined,
  };
};

const unwrapApiData = <T>(response: ApiResponse<T> | T): T => {
  if (response && typeof response === 'object' && 'data' in response) {
    const data = (response as ApiResponse<T>).data;
    if (data !== undefined && data !== null) {
      return data;
    }
  }

  return response as T;
};

export const notifySavedListsChanged = () => {
  window.dispatchEvent(new CustomEvent(SAVED_LISTS_CHANGED_EVENT));
};

export const subscribeToSavedListsChanged = (callback: () => void) => {
  window.addEventListener(SAVED_LISTS_CHANGED_EVENT, callback);
  return () => window.removeEventListener(SAVED_LISTS_CHANGED_EVENT, callback);
};

export const getAllSavedLists = async (): Promise<SavedListDto[]> => {
  const response = await getWithAuth<ApiResponse<BackendSavedListDto[]> | BackendSavedListDto[]>(apiVariables.SavedLists);
  const lists = Array.isArray(response) ? response : response.data;
  return Array.isArray(lists) ? lists.map(normalizeSavedList) : [];
};

export const getSavedListById = async (id: string): Promise<SavedListDto | undefined> => {
  const response = await getWithAuth<ApiResponse<BackendSavedListDto> | BackendSavedListDto>(apiVariables.SavedListById(id));
  const list = unwrapApiData(response);
  return list ? normalizeSavedList(list) : undefined;
};

export const getSavedListsForUser = async (_userId: string): Promise<SavedListDto[]> => {
  return getAllSavedLists();
};

export const createSavedList = async (payload: CreateSavedListRequest): Promise<SavedListDto> => {
  const request = {
    listName: payload.listName.trim(),
    description: payload.description?.trim() || '',
    memberUserIds: payload.memberUserIds.map(String),
  };

  const response = await postWithAuthLocal<ApiResponse<BackendSavedListDto> | BackendSavedListDto>(
    apiVariables.SavedLists,
    request
  );
  const list = unwrapApiData(response);

  if (!list) {
    throw new Error('Private list API returned no data.');
  }

  notifySavedListsChanged();
  return normalizeSavedList(list);
};

export const deleteSavedList = async (id: string): Promise<void> => {
  await deleteWithAuth<ApiResponse<boolean>>(apiVariables.SavedListById(id), '');
  notifySavedListsChanged();
};
